/**
 * PinNodeDialog
 * Lets the user freeze a node's output by supplying PinnedData JSON.
 * When saved, the backend skips executing the node and returns the frozen data.
 *
 * Backend format (PinnedDataDto):
 *   { "success": [{ "data": { "key": "value" } }], "error": [], "conditional": [] }
 */

import React, { useState, useEffect, useRef } from 'react';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { CloseIcon } from '@bizfirst/common-react';
import { ModalPortal } from './ModalPortal';

const PLACEHOLDER = `{
  "success": [
    {
      "data": {
        "result": "your frozen output here"
      }
    }
  ],
  "error": [],
  "conditional": []
}`;

// Lucide pin/thumbtack icon — not in @bizfirst/common-react icon set
const PinIcon = ({ size = 14, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z"/>
  </svg>
);

interface PinNodeDialogProps {
  nodeId: string;
  onClose: () => void;
}

export const PinNodeDialog: React.FC<PinNodeDialogProps> = ({ nodeId, onClose }) => {
  const { nodes, updateProcessElement } = useWorkflowStore();
  const { theme } = useUIStore();
  const isDark = theme !== 'light';

  const node = nodes.find((n) => n.id === nodeId);
  const currentPinned = node?.data.processElement?.pinnedData ?? '';
  const [json, setJson] = useState<string>(currentPinned);
  const [error, setError] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const validate = (value: string): boolean => {
    if (!value.trim()) return true;
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setError('Must be a JSON object');
        return false;
      }
    } catch {
      setError('Invalid JSON');
      return false;
    }
    setError('');
    return true;
  };

  const handleChange = (value: string) => {
    setJson(value);
    if (error) validate(value);
  };

  const handleSave = () => {
    if (!validate(json)) return;
    updateProcessElement(nodeId, { pinnedData: json.trim() || undefined });
    onClose();
  };

  const handleUnpin = () => {
    updateProcessElement(nodeId, { pinnedData: undefined });
    onClose();
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    pointerEvents: 'auto',
  };

  const dialogStyle: React.CSSProperties = {
    background: isDark ? '#0f172a' : '#ffffff',
    border: isDark ? '1px solid rgba(58,140,69,0.3)' : '1px solid #e5e7eb',
    borderRadius: '12px',
    boxShadow: isDark
      ? '0 0 24px rgba(58,140,69,0.25), 0 12px 40px rgba(0,0,0,0.7)'
      : '0 8px 32px rgba(0,0,0,0.15)',
    width: '560px',
    maxWidth: '90vw',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '14px 16px',
    borderBottom: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: isDark ? '#f1f5f9' : '#111827',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  const bodyStyle: React.CSSProperties = {
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    color: isDark ? '#94a3b8' : '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '200px',
    background: isDark ? '#1e293b' : '#f9fafb',
    border: `1px solid ${error ? '#ef4444' : (isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb')}`,
    borderRadius: 6,
    color: isDark ? '#e2e8f0' : '#111827',
    fontFamily: 'monospace',
    fontSize: 12,
    padding: '10px',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const hintStyle: React.CSSProperties = {
    fontSize: 11,
    color: isDark ? '#64748b' : '#9ca3af',
    lineHeight: 1.5,
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#ef4444',
  };

  const footerStyle: React.CSSProperties = {
    padding: '10px 16px',
    borderTop: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid #f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  };

  const btnBase: React.CSSProperties = {
    padding: '7px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
  };

  return (
    <ModalPortal>
      <div style={overlayStyle} onMouseDown={onClose}>
        <div style={dialogStyle} onMouseDown={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <span style={titleStyle}>
              <PinIcon size={15} color={isDark ? '#94a3b8' : '#6b7280'} />
              <span>Pin Node Output</span>
            </span>
            <button
              style={{ ...btnBase, padding: '4px 6px', background: 'transparent', color: isDark ? '#94a3b8' : '#6b7280', display: 'flex', alignItems: 'center' }}
              onClick={onClose}
            >
              <CloseIcon size={16} />
            </button>
          </div>

          <div style={bodyStyle}>
            <div style={hintStyle}>
              Paste the frozen output JSON below. When pinned, the workflow executor skips this node
              and returns the data here directly — useful for testing downstream nodes.
            </div>
            <label style={labelStyle}>Pinned output JSON</label>
            <textarea
              ref={textareaRef}
              style={textareaStyle}
              value={json}
              placeholder={PLACEHOLDER}
              onChange={(e) => handleChange(e.target.value)}
              spellCheck={false}
            />
            {error && <span style={errorStyle}>{error}</span>}
          </div>

          <div style={footerStyle}>
            <div>
              {currentPinned && (
                <button
                  style={{ ...btnBase, background: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: '#ef4444' }}
                  onClick={handleUnpin}
                >
                  Unpin
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ ...btnBase, background: isDark ? 'rgba(255,255,255,0.07)' : '#f3f4f6', color: isDark ? '#e2e8f0' : '#374151' }}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                style={{ ...btnBase, background: '#309B48', color: '#fff' }}
                onClick={handleSave}
              >
                {json.trim() ? 'Pin Output' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
